import React from 'react';
import styled from 'styled-components';
import { useLanguage } from '../../i18n/LanguageContext';
import Section from '../../components/ui/Section';
import Button from '../../components/ui/Button';
import Card from '../../components/ui/Card';
import { Helmet } from 'react-helmet';

const PageContainer = styled.div`
  padding-top: 70px;
`;

const HeroSection = styled.div`
  background-image: url('https://images.unsplash.com/photo-1484154218962-a197022b5858?ixlib=rb-1.2.1&auto=format&fit=crop&w=1350&q=80');
  background-size: cover;
  background-position: center;
  height: 500px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
  }
`;

const HeroContent = styled.div`
  position: relative;
  text-align: center;
  max-width: 800px;
  padding: 0 2rem;
`;

const HeroTitle = styled.h1`
  font-size: 3rem;
  margin-bottom: 1rem;
  color: white;
`;

const HeroSubtitle = styled.p`
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 2rem;
`;

const Content = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 2rem;
`;

const CardsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const Note = styled.div`
  margin-top: 2rem;
  color: var(--text-secondary);
  text-align: center;
`;

const LongTermRentals: React.FC = () => {
  const { language } = useLanguage();
  
  return (
    <PageContainer>
      <Helmet>
        <title>
          {language === 'it' 
            ? 'Affitti Medio/Lungo Termine | Servizi J-O-H' 
            : 'Medium/Long-term Rentals | J-O-H Services'}
        </title>
        <meta 
          name="description" 
          content={language === 'it' 
            ? 'Affitti medio/lungo termine: scopri gli alloggi disponibili e i servizi utili come arredamento e lavanderia.'
            : 'Medium/long-term rentals: browse available accommodations and useful services like furniture and laundry.'}
        />
      </Helmet>
      
      <HeroSection>
        <HeroContent>
          <HeroTitle>
            {language === 'it' ? 'Affitti Medio/Lungo Termine' : 'Medium/Long-term Rentals'}
          </HeroTitle>
          <HeroSubtitle>
            {language === 'it'
              ? 'Una sola pagina per trovare casa e i servizi essenziali.'
              : 'One page to find your home and essential services.'}
          </HeroSubtitle>
          <Button variant="primary" to="/accommodations">
            {language === 'it' ? 'Vedi Alloggi' : 'View Accommodations'}
          </Button>
        </HeroContent>
      </HeroSection>
      
      <Section padding="4rem 0">
        <Content>
          <CardsGrid>
            {[
              {
                it: 'Alloggi',
                en: 'Accommodations',
                subtitleIt: 'Contratti flessibili 1–12 mesi',
                subtitleEn: 'Flexible 1–12 month terms',
                descriptionIt: 'Sfoglia gli appartamenti pronti per essere abitati. Pulizie disponibili su richiesta.',
                descriptionEn: 'Browse move‑in‑ready apartments. Cleaning available on request.',
                tags: ['Arredati', 'Centrali', 'Flessibili'],
                image: 'https://images.unsplash.com/photo-1505692794403-34d4982f88aa?q=80&w=1350&auto=format&fit=crop',
                link: '/accommodations'
              },
              {
                it: 'Arredamento',
                en: 'Furniture',
                subtitleIt: 'Soluzioni su misura',
                subtitleEn: 'Tailored solutions',
                descriptionIt: 'Arreda il tuo alloggio senza pensieri con soluzioni complete. Consegna e montaggio inclusi.',
                descriptionEn: 'Furnish your home hassle‑free with complete solutions. Delivery and setup included.',
                tags: ['Design', 'Servizio', 'Plug‑and‑Play'],
                image: 'https://images.unsplash.com/photo-1493663284031-b7e3aefcae8e?q=80&w=1350&auto=format&fit=crop',
                link: '/services/furniture'
              },
              {
                it: 'Lavanderia',
                en: 'Laundry',
                subtitleIt: 'Ritiro e consegna programmati',
                subtitleEn: 'Scheduled pickup and delivery',
                descriptionIt: 'Lavanderia e noleggio biancheria di qualità alberghiera con ritiro e consegna.',
                descriptionEn: 'Laundry and hotel‑quality linen rental with pickup and delivery.',
                tags: ['Comfort', 'Qualità', 'Servizio'],
                image: 'https://images.unsplash.com/photo-1582582429416-77a96c0f4d9d?q=80&w=1350&auto=format&fit=crop',
                link: '/services/laundry'
              }
            ].map((x, idx) => (
              <Card
                key={x.it}
                image={x.image}
                title={language === 'it' ? x.it : x.en}
                subtitle={language === 'it' ? x.subtitleIt : x.subtitleEn}
                description={language === 'it' ? x.descriptionIt : x.descriptionEn}
                tags={x.tags}
                hoverEffect={true}
                delay={idx * 0.05}
                {...(x.link ? { link: x.link } : {})}
              >
                
              </Card>
            ))}
          </CardsGrid>
          <Note>
            {language === 'it' ? (
              <>
                Hai bisogno di <strong>pulizie</strong>? Offriamo servizio professionale: <a href="/services/cleaning" style={{ color: '#004744' }}>scopri di più</a>.
              </>
            ) : (
              <>
                Need <strong>cleaning</strong>? We provide a professional service: <a href="/services/cleaning" style={{ color: '#004744' }}>learn more</a>.
              </>
            )}
          </Note>
        </Content>
      </Section>
    </PageContainer>
  );
};

export default LongTermRentals; 